<script>
	import { onMount } from 'svelte';
	import Loader from './Loader.svelte';
	import TabView from './TabView.svelte';
	import TabPane from './TabPane.svelte';
	import GameCard from './GameCard.svelte';
	import {
		buildQuery,
		compUrl,
		getApiUrl,
		injectGlobalStyles,
		loadJson,
		normalizeCollection,
		normalizeText,
		playerUrl,
		teamUrl
	} from './shared.js';
	import RoundRobin from './RoundRobin.svelte';
	import Playoff from './Playoff.svelte';

	export let tag = '';
	export let season = '';
	export let lang = 'ru';

	let host;
	let loading = false;
	let standingsLoading = false;
	let calendarLoading = false;
	let statsLoading = false;
	let errorMessage = '';
	let standingsErrorMessage = '';
	let calendarErrorMessage = '';
	let statsErrorMessage = '';
	let comp = null;
	let standingsList = [];
	let calendar = [];
	let stats = [];
	let teamStats = [];
	let coachStats = [];
	let playersSortField = '';
	let playersSortDirection = 'asc';
	let teamsSortField = '';
	let teamsSortDirection = 'asc';
	let coachesSortField = '';
	let coachesSortDirection = 'asc';
	let showAllPlayers = false;

$: children = normalizeCollection(comp?.children);
$: players = normalizeCollection(stats);
$: teams = normalizeCollection(teamStats);
$: coaches = normalizeCollection(coachStats);
$: sortedPlayers = getSortedItems(players, playersSortField, playersSortDirection, ['games', 'win', 'lose', 'start5']);
$: visiblePlayers = showAllPlayers ? sortedPlayers : sortedPlayers.slice(0, 20);
$: sortedTeams = getSortedItems(teams, teamsSortField, teamsSortDirection, ['games', 'homeGames', 'awayGames', 'winGames', 'loseGames']);
$: sortedCoaches = getSortedItems(coaches, coachesSortField, coachesSortDirection, ['games', 'win', 'lose', 'foul']);

function resolveFieldValue(item, field) {
	if (!field) {
		return null;
	}
	if (!field.includes('.')) {
		return item?.[field];
	}
	return field.split('.').reduce((acc, part) => acc?.[part], item);
}

function getSortedItems(items, field, direction, numericFields = []) {
	return [...items].sort((left, right) => {
		if (!field) {
			return 0;
		}

		let leftValue = resolveFieldValue(left, field);
		let rightValue = resolveFieldValue(right, field);

		if (field.startsWith('stats.') || numericFields.includes(field)) {
			leftValue = Number(leftValue) || 0;
			rightValue = Number(rightValue) || 0;
		}

		if (typeof leftValue === 'number' && typeof rightValue === 'number') {
			const compare = leftValue - rightValue;
			return direction === 'asc' ? compare : -compare;
		}

		leftValue = String(leftValue || '');
		rightValue = String(rightValue || '');
		return direction === 'asc'
			? leftValue.localeCompare(rightValue)
			: rightValue.localeCompare(leftValue);
	});
}

	function getCompTitle(data) {
		return data?.name || data?.abcName || data?.shortName || '—';
	}

	function getCompSubtitle(data) {
		return data?.seasonName || data?.season || '—';
	}

	function getInitials(data) {
		return (data?.abcName?.[0] || data?.shortName?.[0] || data?.name?.[0] || 'C').toUpperCase();
	}

	function getCompType(standing) {
		return standing?.comp?.compType || standing?.comp?.CompType || '';
	}

	function isAllowedCompType(standing) {
		const type = getCompType(standing);
		return ['RoundRobin', 'Playoff'].includes(type);
	}

	function getStandingTitle(standing) {
		return standing?.comp?.name || standing?.comp?.Name || standing?.competition?.title || standing?.competition?.Title || '';
	}

	function getPersonTitle(person) {
		return [person?.lastName, person?.firstName, person?.secondName].filter(Boolean).join(' ') || '—';
	}

	function getPersonInitials(person, fallback = 'P') {
		const first = person?.firstName?.[0] || '';
		const last = person?.lastName?.[0] || '';
		return `${first}${last}`.trim() || fallback;
	}

	function getTeamTitle(data) {
		return data?.name || data?.shortName || data?.abcName || '—';
	}

	function getTeamInitials(data) {
		return (data?.label?.[0] || data?.shortName?.[0] || data?.name?.[0] || 'T').toUpperCase();
	}

	function togglePlayersSort(field) {
		if (playersSortField === field) {
			playersSortDirection = playersSortDirection === 'asc' ? 'desc' : 'asc';
			return;
		}
		playersSortField = field;
		playersSortDirection = 'asc';
	}

	function toggleTeamsSort(field) {
		if (teamsSortField === field) {
			teamsSortDirection = teamsSortDirection === 'asc' ? 'desc' : 'asc';
			return;
		}
		teamsSortField = field;
		teamsSortDirection = 'asc';
	}

	function toggleCoachesSort(field) {
		if (coachesSortField === field) {
			coachesSortDirection = coachesSortDirection === 'asc' ? 'desc' : 'asc';
			return;
		}
		coachesSortField = field;
		coachesSortDirection = 'asc';
	}

	function getSortLabel(field, label) {
		if (playersSortField !== field) {
			return label;
		}
		return `${label} ${playersSortDirection === 'asc' ? '↑' : '↓'}`;
	}

	function getTeamsSortLabel(field, label) {
		if (teamsSortField !== field) {
			return label;
		}
		return `${label} ${teamsSortDirection === 'asc' ? '↑' : '↓'}`;
	}

	function getCoachesSortLabel(field, label) {
		if (coachesSortField !== field) {
			return label;
		}
		return `${label} ${coachesSortDirection === 'asc' ? '↑' : '↓'}`;
	}

	function togglePlayersVisibility() {
		showAllPlayers = !showAllPlayers;
	}

	function openPlayer(person) {
		if (!person?.personId) {
			return;
		}
		window.location.href = playerUrl(person.personId, {
			tag,
			teamId: person.team?.id,
			compId: tag
		});
	}

	function openTeam(team) {
		if (!team?.id) {
			return;
		}
		window.location.href = teamUrl(team.id, {
			tag,
			compId: tag
		});
	}

	function openChildComp(childTag, childSeason) {
		if (!childTag) {
			return;
		}
		window.location.href = compUrl(childTag, { season: childSeason, lang });
	}

	async function loadComp() {
		if (!tag) {
			errorMessage = 'Не указан тег турнира';
			comp = null;
			standingsList = [];
			calendar = [];
			return;
		}

		loading = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/ib/competition/${encodeURIComponent(tag)}/info${buildQuery({ season, lang })}`;
			comp = await loadJson(url);
		} catch (error) {
			console.error('Load comp info error:', error);
			errorMessage = 'Ошибка загрузки турнира';
			comp = null;
			standingsList = [];
			calendar = [];
		} finally {
			loading = false;
		}
	}

	async function loadStandings() {
		if (!tag) {
			standingsList = [];
			return;
		}

		standingsLoading = true;
		standingsErrorMessage = '';
		try {
			const url = `${getApiUrl()}/ib/competition/${encodeURIComponent(tag)}/standings${buildQuery({ season, lang })}`;
			standingsList = normalizeCollection(await loadJson(url)).filter(isAllowedCompType);
		} catch (error) {
			console.error('Load standings error:', error);
			standingsErrorMessage = 'Ошибка загрузки положения команд';
			standingsList = [];
		} finally {
			standingsLoading = false;
		}
	}

	async function loadCalendar() {
		if (!tag) {
			calendar = [];
			return;
		}

		calendarLoading = true;
		calendarErrorMessage = '';
		try {
			const url = `${getApiUrl()}/ib/competition/${encodeURIComponent(tag)}/calendar${buildQuery({ season, lang })}`;
			const calendarData = await loadJson(url);
			calendar = Array.isArray(calendarData) ? calendarData : [];
		} catch (error) {
			console.error('Load calendar error:', error);
			calendarErrorMessage = 'Ошибка загрузки расписания';
			calendar = [];
		} finally {
			calendarLoading = false;
		}
	}

	async function loadStats() {
		if (!tag) {
			stats = [];
			teamStats = [];
			coachStats = [];
			return;
		}

		statsLoading = true;
		statsErrorMessage = '';
		try {
			const query = buildQuery({ season, lang });
			const base = `${getApiUrl()}/ib/competition/${encodeURIComponent(tag)}`;
			const [playerStatsData, teamStatsData, coachStatsData] = await Promise.all([
				loadJson(`${base}/player-stats${query}`),
				loadJson(`${base}/team-stats${query}`),
				loadJson(`${base}/coach-stats${query}`)
			]);
			stats = Array.isArray(playerStatsData) ? playerStatsData : [];
			teamStats = Array.isArray(teamStatsData) ? teamStatsData : [];
			coachStats = Array.isArray(coachStatsData) ? coachStatsData : [];
		} catch (error) {
			console.error('Load stats error:', error);
			statsErrorMessage = 'Ошибка загрузки статистики';
			stats = [];
			teamStats = [];
			coachStats = [];
		} finally {
			statsLoading = false;
		}
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) {
			injectGlobalStyles(root);
		}

		loadComp();
		loadStandings();
		loadCalendar();
		loadStats();
	});
</script>

<div class="widget-card" bind:this={host}>
	{#if loading}
		<div class="widget-card__status"><Loader size="64px" /></div>
	{:else if errorMessage}
		<div class="widget-card__status">{errorMessage}</div>
	{:else if comp}
		<div class="profile">
			<div class="profile__head">
				<div class="profile__media">
					{#if comp.logo}
						<img class="profile__image" src={comp.logo} alt={getCompTitle(comp)}>
					{:else}
						<div class="profile__placeholder">{getInitials(comp)}</div>
					{/if}
				</div>

				<div class="profile__title">
					<h3>{getCompTitle(comp)}</h3>
					<p>{getCompSubtitle(comp)}</p>
				</div>
			</div>

			{#if children.length > 0}
				<div class="child-comps">
					<h4>Дочерние турниры</h4>
					<div class="child-comps-list">
						{#each children as child}
							<div class="child-comp-card" role="button" tabindex="0" on:click={() => openChildComp(child.tag, child.season)} on:keypress={() => openChildComp(child.tag, child.season)}>
								<div class="child-comp-card__title">{getCompTitle(child)}</div>
								<div class="child-comp-card__subtitle">{getCompSubtitle(child)}</div>
							</div>
						{/each}
					</div>
				</div>
			{/if}

			<TabView>
				<TabPane title="Положение команд">
					<div class="widget-tabs__body">
						{#if standingsLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if standingsErrorMessage}
							<div class="widget-card__status">{standingsErrorMessage}</div>
						{:else if !standingsList.length}
							<div class="widget-card__status --small">Положение команд не найдено</div>
						{:else}
							{#each standingsList as standing, index}
								<div class="standings__group">
									{#if getStandingTitle(standing)}
										<h3 class="standings__subtitle">{getStandingTitle(standing)}</h3>
									{/if}
									{#if getCompType(standing) === 'RoundRobin'}
										<RoundRobin standings={standing.standings} {tag} compId={standing?.comp?.id} />
									{:else if getCompType(standing) === 'Playoff'}
										<Playoff playoffPairs={standing.playoffPairs} {tag} compId={standing?.comp?.id} />
									{:else}
										<div class="widget-card__status --small">Тип турнира не поддерживается</div>
									{/if}
								</div>
							{/each}
						{/if}
					</div>
				</TabPane>

				<TabPane title="Расписание">
					<div class="widget-tabs__body">
						{#if calendarLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if calendarErrorMessage}
							<div class="widget-card__status">{calendarErrorMessage}</div>
						{:else if !calendar.length}
							<div class="widget-card__status --small">Матчи не найдены</div>
						{:else}
							<div class="games-list">
								{#each calendar as game}
									<GameCard {game} tag={tag} compId={tag} />
								{/each}
							</div>
						{/if}
					</div>
				</TabPane>

				<TabPane title="Статистика">
					<div class="widget-tabs__body">
						{#if statsLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if statsErrorMessage}
							<div class="widget-card__status">{statsErrorMessage}</div>
						{:else if !sortedPlayers.length && !sortedTeams.length && !sortedCoaches.length}
							<div class="widget-card__status --small">Статистика не найдена</div>
						{:else}
							<h3 class="stats-section__title">Игроки</h3>
							{#if sortedPlayers.length}
								<div class="stats_table-wrap stats-section">
									<table class="stats_table players-table">
										<thead>
											<tr>
												<th rowspan="2">Игрок</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'games'} on:click={() => togglePlayersSort('games')}>
													{getSortLabel('games', 'Игр')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'start5'} on:click={() => togglePlayersSort('start5')}>
													{getSortLabel('start5', '5')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.playedTime'} on:click={() => togglePlayersSort('stats.playedTime')}>
													{getSortLabel('stats.playedTime', 'СВ')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.points'} on:click={() => togglePlayersSort('stats.points')}>
													{getSortLabel('stats.points', 'Очки')}
												</th>
												<th colspan="3">Броски</th>
												<th colspan="3">Подборы</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.assist'} on:click={() => togglePlayersSort('stats.assist')}>
													{getSortLabel('stats.assist', 'ГП')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.steal'} on:click={() => togglePlayersSort('stats.steal')}>
													{getSortLabel('stats.steal', 'ПХ')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.turnover'} on:click={() => togglePlayersSort('stats.turnover')}>
													{getSortLabel('stats.turnover', 'ПТ')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.blockShot'} on:click={() => togglePlayersSort('stats.blockShot')}>
													{getSortLabel('stats.blockShot', 'БШ')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.foul'} on:click={() => togglePlayersSort('stats.foul')}>
													{getSortLabel('stats.foul', 'Ф')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.foulsOnPlayer'} on:click={() => togglePlayersSort('stats.foulsOnPlayer')}>
													{getSortLabel('stats.foulsOnPlayer', 'ФС')}
												</th>
												<th rowspan="2" class="is-sortable" class:is-sorted={playersSortField === 'stats.plusMinus'} on:click={() => togglePlayersSort('stats.plusMinus')}>
													{getSortLabel('stats.plusMinus', '+/-')}
												</th>
											</tr>
											<tr>
												<th class="is-sortable" class:is-sorted={playersSortField === 'stats.goal2'} on:click={() => togglePlayersSort('stats.goal2')}>
													{getSortLabel('stats.goal2', '2-очк')}
												</th>
												<th class="is-sortable" class:is-sorted={playersSortField === 'stats.goal3'} on:click={() => togglePlayersSort('stats.goal3')}>
													{getSortLabel('stats.goal3', '3-очк')}
												</th>
												<th class="is-sortable" class:is-sorted={playersSortField === 'stats.goal1'} on:click={() => togglePlayersSort('stats.goal1')}>
													{getSortLabel('stats.goal1', 'ШБ')}
												</th>
												<th class="is-sortable" class:is-sorted={playersSortField === 'stats.defRebound'} on:click={() => togglePlayersSort('stats.defRebound')}>
													{getSortLabel('stats.defRebound', 'СЩ')}
												</th>
												<th class="is-sortable" class:is-sorted={playersSortField === 'stats.offRebound'} on:click={() => togglePlayersSort('stats.offRebound')}>
													{getSortLabel('stats.offRebound', 'ЧЩ')}
												</th>
												<th class="is-sortable" class:is-sorted={playersSortField === 'stats.rebound'} on:click={() => togglePlayersSort('stats.rebound')}>
													{getSortLabel('stats.rebound', 'ВС')}
												</th>
											</tr>
										</thead>
										<tbody>
											{#each visiblePlayers as player}
												<tr class="players-table__row">
													<td class="players-table__cell-player">
														<div class="player-cell">
															<div class="player-cell__photo">
																{#if player.person?.photo}
																	<img class="player-cell__image" src={player.person.photo} alt={getPersonTitle(player.person)}>
																{:else}
																	<div class="player-cell__placeholder">{getPersonInitials(player.person, 'P')}</div>
																{/if}
															</div>
															<div class="player-cell__info">
																<div class="player-cell__name" role="button" tabindex="0" on:click={() => openPlayer(player)} on:keypress={() => openPlayer(player)}>
																	{getPersonTitle(player.person)}
																</div>
																<div class="player-cell__team" role="button" tabindex="0" on:click={() => openTeam(player.team)} on:keypress={() => openTeam(player.team)}>
																	{#if player.team?.logo}
																		<img class="player-cell__team-logo" src={player.team.logo} alt={getTeamTitle(player.team)}>
																	{:else}
																		<div class="player-cell__team-placeholder">{getTeamInitials(player.team)}</div>
																	{/if}
																	<span class="player-cell__team-name">{getTeamTitle(player.team)}</span>
																</div>
															</div>
														</div>
													</td>
													<td class:is-sorted={playersSortField === 'games'}>{player.games || '—'}</td>
													<td class:is-sorted={playersSortField === 'start5'}>{player.start5 || '—'}</td>
													<td class:is-sorted={playersSortField === 'stats.playedTime'}>{normalizeText(player.stats?.playedTime)}</td>
													<td class:is-sorted={playersSortField === 'stats.points'}>{normalizeText(player.stats?.points)}</td>
													<td class:is-sorted={playersSortField === 'stats.goal2'}>{normalizeText(player.stats?.goal2)}</td>
													<td class:is-sorted={playersSortField === 'stats.goal3'}>{normalizeText(player.stats?.goal3)}</td>
													<td class:is-sorted={playersSortField === 'stats.goal1'}>{normalizeText(player.stats?.goal1)}</td>
													<td class:is-sorted={playersSortField === 'stats.defRebound'}>{normalizeText(player.stats?.defRebound)}</td>
													<td class:is-sorted={playersSortField === 'stats.offRebound'}>{normalizeText(player.stats?.offRebound)}</td>
													<td class:is-sorted={playersSortField === 'stats.rebound'}>{normalizeText(player.stats?.rebound)}</td>
													<td class:is-sorted={playersSortField === 'stats.assist'}>{normalizeText(player.stats?.assist)}</td>
													<td class:is-sorted={playersSortField === 'stats.steal'}>{normalizeText(player.stats?.steal)}</td>
													<td class:is-sorted={playersSortField === 'stats.turnover'}>{normalizeText(player.stats?.turnover)}</td>
													<td class:is-sorted={playersSortField === 'stats.blockShot'}>{normalizeText(player.stats?.blockShot)}</td>
													<td class:is-sorted={playersSortField === 'stats.foul'}>{normalizeText(player.stats?.foul)}</td>
													<td class:is-sorted={playersSortField === 'stats.foulsOnPlayer'}>{normalizeText(player.stats?.foulsOnPlayer)}</td>
													<td class:is-sorted={playersSortField === 'stats.plusMinus'}>{normalizeText(player.stats?.plusMinus)}</td>
												</tr>
											{/each}
										</tbody>
									</table>
								</div>
								{#if sortedPlayers.length > 20}
									<div class="players-toggle-wrap">
										<button type="button" class="players-toggle" on:click={togglePlayersVisibility}>
											{showAllPlayers ? 'Скрыть' : 'Показать остальное'}
										</button>
									</div>
								{/if}
							{:else}
								<div class="widget-card__status --small">Статистика игроков не найдена</div>
							{/if}

							<h3 class="stats-section__title">Команды</h3>
							{#if sortedTeams.length}
								<div class="stats_table-wrap stats-section">
									<table class="stats_table teams-table">
										<thead>
											<tr>
												<th>Команда</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'games'} on:click={() => toggleTeamsSort('games')}>{getTeamsSortLabel('games', 'Игр')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'homeGames'} on:click={() => toggleTeamsSort('homeGames')}>{getTeamsSortLabel('homeGames', 'Дома')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'awayGames'} on:click={() => toggleTeamsSort('awayGames')}>{getTeamsSortLabel('awayGames', 'В гостях')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'winGames'} on:click={() => toggleTeamsSort('winGames')}>{getTeamsSortLabel('winGames', 'Победы')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'loseGames'} on:click={() => toggleTeamsSort('loseGames')}>{getTeamsSortLabel('loseGames', 'Поражения')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'avgPlayedTime'} on:click={() => toggleTeamsSort('avgPlayedTime')}>{getTeamsSortLabel('avgPlayedTime', 'СВ')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'stats.points'} on:click={() => toggleTeamsSort('stats.points')}>{getTeamsSortLabel('stats.points', 'Очки')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'stats.rebound'} on:click={() => toggleTeamsSort('stats.rebound')}>{getTeamsSortLabel('stats.rebound', 'Подборы')}</th>
												<th class="is-sortable" class:is-sorted={teamsSortField === 'stats.assist'} on:click={() => toggleTeamsSort('stats.assist')}>{getTeamsSortLabel('stats.assist', 'Передачи')}</th>
											</tr>
										</thead>
										<tbody>
											{#each sortedTeams as row}
												<tr>
													<td class="teams-table__cell-team">
														<div class="team-main-cell" role="button" tabindex="0" on:click={() => openTeam(row.team)} on:keypress={() => openTeam(row.team)}>
															<div class="team-main-cell__logo-wrap">
																{#if row.team?.logo}
																	<img class="team-main-cell__logo" src={row.team.logo} alt={getTeamTitle(row.team)}>
																{:else}
																	<div class="team-main-cell__placeholder">{getTeamInitials(row.team)}</div>
																{/if}
															</div>
															<span class="player-cell__name">{getTeamTitle(row.team)}</span>
														</div>
													</td>
													<td class:is-sorted={teamsSortField === 'games'}>{normalizeText(row.games)}</td>
													<td class:is-sorted={teamsSortField === 'homeGames'}>{normalizeText(row.homeGames)}</td>
													<td class:is-sorted={teamsSortField === 'awayGames'}>{normalizeText(row.awayGames)}</td>
													<td class:is-sorted={teamsSortField === 'winGames'}>{normalizeText(row.winGames)}</td>
													<td class:is-sorted={teamsSortField === 'loseGames'}>{normalizeText(row.loseGames)}</td>
													<td class:is-sorted={teamsSortField === 'avgPlayedTime'}>{normalizeText(row.avgPlayedTime)}</td>
													<td class:is-sorted={teamsSortField === 'stats.points'}>{normalizeText(row.stats?.points)}</td>
													<td class:is-sorted={teamsSortField === 'stats.rebound'}>{normalizeText(row.stats?.rebound)}</td>
													<td class:is-sorted={teamsSortField === 'stats.assist'}>{normalizeText(row.stats?.assist)}</td>
												</tr>
											{/each}
										</tbody>
									</table>
								</div>
							{:else}
								<div class="widget-card__status --small">Статистика команд не найдена</div>
							{/if}

							<h3 class="stats-section__title">Тренеры</h3>
							{#if sortedCoaches.length}
								<div class="stats_table-wrap stats-section">
									<table class="stats_table coaches-table">
										<thead>
											<tr>
												<th>Тренер</th>
												<th class="is-sortable" class:is-sorted={coachesSortField === 'games'} on:click={() => toggleCoachesSort('games')}>{getCoachesSortLabel('games', 'Игр')}</th>
												<th class="is-sortable" class:is-sorted={coachesSortField === 'win'} on:click={() => toggleCoachesSort('win')}>{getCoachesSortLabel('win', 'Победы')}</th>
												<th class="is-sortable" class:is-sorted={coachesSortField === 'lose'} on:click={() => toggleCoachesSort('lose')}>{getCoachesSortLabel('lose', 'Поражения')}</th>
												<th class="is-sortable" class:is-sorted={coachesSortField === 'foul'} on:click={() => toggleCoachesSort('foul')}>{getCoachesSortLabel('foul', 'Ф')}</th>
											</tr>
										</thead>
										<tbody>
											{#each sortedCoaches as coach}
												<tr>
													<td class="coaches-table__cell-person">
														<div class="player-cell">
															<div class="player-cell__photo">
																{#if coach.person?.photo}
																	<img class="player-cell__image" src={coach.person.photo} alt={getPersonTitle(coach.person)}>
																{:else}
																	<div class="player-cell__placeholder">{getPersonInitials(coach.person, 'C')}</div>
																{/if}
															</div>
															<div class="player-cell__info">
																<div class="player-cell__name">{getPersonTitle(coach.person)}</div>
																<div class="player-cell__team" role="button" tabindex="0" on:click={() => openTeam(coach.team)} on:keypress={() => openTeam(coach.team)}>
																	{#if coach.team?.logo}
																		<img class="player-cell__team-logo" src={coach.team.logo} alt={getTeamTitle(coach.team)}>
																	{:else}
																		<div class="player-cell__team-placeholder">{getTeamInitials(coach.team)}</div>
																	{/if}
																	<span class="player-cell__team-name">{getTeamTitle(coach.team)}</span>
																</div>
															</div>
														</div>
													</td>
													<td class:is-sorted={coachesSortField === 'games'}>{normalizeText(coach.games)}</td>
													<td class:is-sorted={coachesSortField === 'win'}>{normalizeText(coach.win)}</td>
													<td class:is-sorted={coachesSortField === 'lose'}>{normalizeText(coach.lose)}</td>
													<td class:is-sorted={coachesSortField === 'foul'}>{normalizeText(coach.foul)}</td>
												</tr>
											{/each}
										</tbody>
									</table>
								</div>
							{:else}
								<div class="widget-card__status --small">Статистика тренеров не найдена</div>
							{/if}
						{/if}
					</div>
				</TabPane>
			</TabView>
		</div>
	{/if}
</div>

<style>
	:host { display: block; }
	.widget-card {
		border: 1px solid #d7dce2;
		border-radius: 16px;
		padding: 18px;
		background: #fff;
		box-shadow: 0 6px 24px rgba(8, 22, 47, 0.05);
	}
	.widget-card__status {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 160px;
		color: #53657b;
		text-align: center;
	}
	.widget-card__status.--small {
		min-height: 88px;
	}
	.profile__head {
		display: flex;
		gap: 16px;
		align-items: center;
		margin-bottom: 16px;
	}
	.profile__media { flex: 0 0 auto; }
	.profile__image,
	.profile__placeholder {
		width: 88px;
		height: 88px;
		border-radius: 18px;
		object-fit: cover;
		background: #edf2f7;
	}
	.profile__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 28px;
		font-weight: 700;
		color: #00244d;
	}
	.profile__title h3 {
		margin: 0;
		font-size: 22px;
		color: #122033;
	}
	.profile__title p {
		margin: 6px 0 0;
		color: #53657b;
	}
	.child-comps {
		margin: 16px 0;
	}
	.child-comps h4 {
		margin: 0 0 12px;
		font-size: 18px;
		color: #122033;
	}
	.child-comps-list {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 12px;
	}
	.child-comp-card {
		border: 1px solid #d7dce2;
		border-radius: 12px;
		padding: 12px;
		background: #fff;
		cursor: pointer;
		transition: box-shadow 0.2s ease, transform 0.2s ease;
	}
	.child-comp-card:hover {
		box-shadow: 0 6px 18px rgba(0, 36, 77, 0.16);
		transform: translateY(-2px);
	}
	.child-comp-card__title {
		font-size: 15px;
		font-weight: 600;
		color: #122033;
		margin-bottom: 4px;
	}
	.child-comp-card__subtitle {
		font-size: 13px;
		color: #53657b;
	}
	.widget-tabs__body {
		padding: 4px 0 0;
	}
	.standings__subtitle {
		margin: 8px 0 12px;
		font-size: 16px;
		font-weight: 600;
	}
	.standings__group {
		margin-bottom: 28px;
	}
	.standings__group:last-child {
		margin-bottom: 0;
	}
	.widget-stats {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
		margin: 0;
	}
	.widget-stats div {
		padding: 12px 14px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.widget-stats dt {
		margin-bottom: 6px;
		font-size: 12px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.widget-stats dd {
		margin: 0;
		color: #122033;
		font-weight: 600;
	}
	.games-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
	.roster-table__sort {
		border: none;
		background: transparent;
		font: inherit;
		font-weight: 700;
		color: inherit;
		cursor: pointer;
		padding: 0;
	}
	.players-table th.is-sortable {
		cursor: pointer;
		user-select: none;
		transition: background-color 0.15s ease;
	}
	.players-table th.is-sortable:hover {
		background: #eef3fb;
	}
	.stats-section {
		margin-bottom: 20px;
	}
	.stats-section__title {
		margin: 10px 0 12px;
		font-size: 18px;
		font-weight: 700;
		color: #122033;
	}
	.players-table,
	.teams-table,
	.coaches-table {
		min-width: 1000px;
	}
	.players-table__row {
		cursor: pointer;
	}
	.players-table__cell-player,
	.teams-table__cell-team,
	.coaches-table__cell-person {
		text-align: left;
		padding: 0 !important;
	}
	.player-cell {
		display: flex;
		gap: 8px;
		align-items: center;
		padding: 8px 10px;
	}
	.player-cell__photo {
		flex: 0 0 auto;
	}
	.player-cell__image,
	.player-cell__placeholder {
		width: 40px;
		height: 40px;
		border-radius: 8px;
		object-fit: cover;
		background: #edf2f7;
	}
	.player-cell__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 13px;
		font-weight: 700;
		color: #00244d;
	}
	.player-cell__info {
		flex: 1;
		min-width: 0;
	}
	.player-cell__name {
		font-weight: 600;
		color: #122033;
		cursor: pointer;
		transition: color 0.2s ease;
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 13px;
	}
	.player-cell__name:hover {
		color: #00244d;
	}
	.player-cell__team {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-top: 3px;
		cursor: pointer;
		transition: opacity 0.2s ease;
	}
	.player-cell__team:hover {
		opacity: 0.75;
	}
	.player-cell__team-logo,
	.player-cell__team-placeholder {
		width: 14px;
		height: 14px;
		border-radius: 3px;
		object-fit: cover;
		background: #edf2f7;
		flex: 0 0 14px;
	}
	.player-cell__team-placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 8px;
		font-weight: 700;
		color: #00244d;
	}
	.player-cell__team-name {
		font-size: 11px;
		color: #53657b;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.team-main-cell {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px 10px;
		cursor: pointer;
	}
	.team-main-cell__logo-wrap {
		flex: 0 0 auto;
	}
	.team-main-cell__logo,
	.team-main-cell__placeholder {
		width: 28px;
		height: 28px;
		border-radius: 7px;
		object-fit: cover;
		background: #edf2f7;
	}
	.team-main-cell__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 11px;
		font-weight: 700;
		color: #00244d;
	}
	.players-toggle-wrap {
		display: flex;
		justify-content: center;
		margin-top: 10px;
	}
	.players-toggle {
		border: 1px solid #d7dce2;
		background: #fff;
		border-radius: 10px;
		padding: 8px 14px;
		font-size: 13px;
		font-weight: 600;
		color: #122033;
		cursor: pointer;
	}
	.players-table th.is-sorted,
	.players-table td.is-sorted,
	.teams-table th.is-sorted,
	.teams-table td.is-sorted,
	.coaches-table th.is-sorted,
	.coaches-table td.is-sorted {
		background: #eaf1ff;
	}
	.players-table th.is-sorted,
	.teams-table th.is-sorted,
	.coaches-table th.is-sorted {
		font-weight: 800;
		color: #0b4fa8;
	}
	.players-table td.is-sorted,
	.teams-table td.is-sorted,
	.coaches-table td.is-sorted {
		font-weight: 700;
		color: #0b4fa8;
	}
	@media (max-width: 1200px) {
		.child-comps-list { grid-template-columns: repeat(3, minmax(0, 1fr)); }
		.players-table,
		.teams-table,
		.coaches-table { min-width: 100%; }
	}
	@media (max-width: 900px) {
		.child-comps-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
		.stats_table th:nth-child(n+7) {
			font-size: 10px;
			padding: 6px 4px;
		}
		.stats_table td:nth-child(n+7) {
			font-size: 11px;
			padding: 8px 4px;
		}
		.players-table th:nth-child(n+9) {
			display: none;
		}
		.players-table td:nth-child(n+9) {
			display: none;
		}
	}
	@media (max-width: 640px) {
		.profile__head { align-items: flex-start; flex-direction: column; }
		.widget-stats,
		.child-comps-list { grid-template-columns: 1fr; }
		.players-table th:nth-child(n+5) {
			display: none;
		}
		.players-table td:nth-child(n+5) {
			display: none;
		}
		.team-main-cell__logo,
		.team-main-cell__placeholder {
			width: 24px;
			height: 24px;
		}
	}
</style>
